// FundingApplications — the "Applications" tab on a client's file.
//
// Three weapons in one place:
//   1. Recommended applications — the recommendation engine's output for the
//      personal path (loans + cards) and business path (business cards),
//      with tier badges, plain-English reasons, and the compliance warnings
//      (reports-to-personal, 5/24, stretch apps) rendered verbatim.
//   2. Send application packet — email a lender a funding packet with client
//      docs attached from the file.
//   3. Business Credit Builder — the tier-ladder checklist, staff-checkable,
//      mirrored read-only in the client portal.
// Plus the soft-pull button when the tenant has iSoftpull connected.
import { useEffect, useState } from "react";
import { Sparkles, Send, Hammer, ShieldAlert, ExternalLink, CheckCircle2, RefreshCw, Eye, EyeOff, Plus, Wrench } from "lucide-react";
import { fundingApps, lenders as lendersApi, company as companyApi, clients as clientsApi, creditProducts } from "../lib/api";
import { Modal } from "./Modal";

const TIER_STYLE: Record<string, { label: string; cls: string }> = {
  strong: { label: "Strong match", cls: "pill-green" },
  match: { label: "Match", cls: "" },
  reach: { label: "Stretch", cls: "pill-red" },
};

export function FundingApplications({ clientId }: { clientId: string }) {
  const [recs, setRecs] = useState<any | null>(null);
  const [path, setPath] = useState<"both" | "personal" | "business">("both");
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);

  // soft pull
  const [softpullOn, setSoftpullOn] = useState(false);
  // null = unknown yet. false = no FCRA authorization on file, so a pull would
  // have no permissible purpose and the API will (correctly) refuse it.
  const [creditAuthorized, setCreditAuthorized] = useState<boolean | null>(null);
  const [pulling, setPulling] = useState(false);

  // packet dialog
  const [packetOpen, setPacketOpen] = useState(false);
  const [lenderList, setLenderList] = useState<any[]>([]);
  const [uploads, setUploads] = useState<any[]>([]);
  const [packets, setPackets] = useState<any[]>([]);
  const [pLender, setPLender] = useState("");
  const [pEmail, setPEmail] = useState("");
  const [pNote, setPNote] = useState("");
  const [pDocs, setPDocs] = useState<Set<string>>(new Set());
  const [pBusy, setPBusy] = useState(false);

  // builder
  const [builder, setBuilder] = useState<any | null>(null);

  // credit repair tag + handoff
  const [repair, setRepair] = useState<any | null>(null);
  const [repairBusy, setRepairBusy] = useState(false);

  // portal visibility + add-product
  const [offersEnabled, setOffersEnabled] = useState<boolean | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [np, setNp] = useState<any>({ product_type: "business_card", name: "", issuer: "", min_score: "680", application_url: "", prequal_url: "", reports_to_personal: false, highlights: "" });
  const [addBusy, setAddBusy] = useState(false);
  const [showAll, setShowAll] = useState<Record<string, boolean>>({});

  async function loadRecs(p = path, online = onlineOnly) {
    setLoading(true);
    try {
      const r = await fundingApps.recommendations(clientId, p, online);
      setRecs(r);
      if (typeof (r as any).portal_offers_enabled === "boolean") setOffersEnabled((r as any).portal_offers_enabled);
    }
    catch (e: any) { setMsg(e?.message || "Could not load recommendations."); }
    finally { setLoading(false); }
  }

  async function togglePortalOffers() {
    const next = !offersEnabled;
    setOffersEnabled(next);
    try { await clientsApi.update(clientId, { portal_offers_enabled: next } as any); }
    catch (e: any) { setOffersEnabled(!next); setMsg(e?.message || "Could not update portal visibility."); }
  }

  async function addProduct() {
    setAddBusy(true); setMsg(null);
    try {
      await creditProducts.create({ ...np, min_score: Number(np.min_score) });
      setAddOpen(false);
      setNp({ product_type: "business_card", name: "", issuer: "", min_score: "680", application_url: "", prequal_url: "", reports_to_personal: false, highlights: "" });
      setMsg("Product added — it now ranks in this company's recommendations.");
      loadRecs();
    } catch (e: any) { setMsg(e?.message || "Could not add the product."); }
    finally { setAddBusy(false); }
  }
  useEffect(() => {
    loadRecs();
    fundingApps.repairStatus(clientId).then(setRepair).catch(() => {});
    fundingApps.builder(clientId).then(setBuilder).catch(() => {});
    fundingApps.packetHistory(clientId).then((r) => setPackets(r.packets)).catch(() => {});
    companyApi.get().then((r: any) => setSoftpullOn(!!r.settings?.softpull_configured)).catch(() => {});
    clientsApi.get(clientId).then((c: any) => setCreditAuthorized(!!c?.credit_authorization)).catch(() => {});
  }, [clientId]);

  async function runSoftpull() {
    if (!confirm("Run a soft pull on this client through iSoftpull? No score impact, but it's a real bureau request.")) return;
    setPulling(true); setMsg(null);
    try {
      const r = await fundingApps.softpull(clientId);
      const s = Object.entries(r.scores || {}).map(([m, v]) => `${m}: ${v}`).join(" · ");
      setMsg(`Soft pull complete — ${s || "report retrieved (scores in the stored report)"}.`);
    } catch (e: any) { setMsg(`Soft pull failed: ${e?.message}`); }
    finally { setPulling(false); }
  }

  async function openPacket() {
    setPacketOpen(true);
    try {
      const [ls, us] = await Promise.all([lendersApi.list({ status: "active" }), fundingApps.uploads(clientId)]);
      setLenderList(ls as any[]); setUploads(us.uploads || []);
    } catch { /* lists stay empty */ }
  }

  async function sendPacket() {
    setPBusy(true); setMsg(null);
    // Identity/tax paperwork is off by default. If the API flags one, name the
    // file and ask — a relationship manager does sometimes need it, but it
    // should never go out because someone ticked a box without looking.
    const acknowledged = new Set<string>();
    try {
      for (let attempt = 0; attempt < 12; attempt++) {
        try {
          const r = await fundingApps.sendPacket(clientId, {
            confirm: true,
            lender_id: pLender || undefined,
            to_email: pEmail.trim(),
            doc_ids: [...pDocs],
            // Checking a document in the dialog IS the review confirmation — the
            // API refuses any attachment not individually confirmed SSN/DOB-free.
            reviewed_doc_ids: [...pDocs],
            acknowledged_sensitive_doc_ids: [...acknowledged],
            note: pNote.trim() || undefined,
          });
          setMsg(`Application packet sent to ${r.packet.sent_to} (${r.packet.attached} document(s) attached).`);
          setPacketOpen(false); setPEmail(""); setPNote(""); setPDocs(new Set());
          fundingApps.packetHistory(clientId).then((h) => setPackets(h.packets)).catch(() => {});
          return;
        } catch (e: any) {
          const code = e?.body?.error;
          const docId = e?.body?.doc_id;
          const filename = e?.body?.filename;
          if (code !== "identity_doc_needs_ack" || !docId || acknowledged.has(docId)) throw e;
          const ok = window.confirm(
            `"${filename}" looks like an identity or tax document — it normally contains an SSN and date of birth.\n\n` +
            `Send it anyway? Only do this if your banking relationship manager asked for it. ` +
            `It will be recorded in the audit log against your name.`,
          );
          if (!ok) { setMsg(`Not sent — uncheck "${filename}" and send again.`); return; }
          acknowledged.add(String(docId));
        }
      }
      setMsg("Packet failed to send.");
    } catch (e: any) { setMsg(e?.message || "Packet failed to send."); }
    finally { setPBusy(false); }
  }

  async function repairTag(tag: boolean) {
    setRepairBusy(true);
    try { await fundingApps.repairTag(clientId, tag); setRepair(await fundingApps.repairStatus(clientId)); }
    catch (e: any) { setMsg(e?.message || "Could not update the tag."); }
    finally { setRepairBusy(false); }
  }

  async function repairHandoff() {
    if (!confirm("Send this client to Alief Dispute Hub? They'll be created over there with a summary of the negatives from the analysis.")) return;
    setRepairBusy(true); setMsg(null);
    try {
      const r = await fundingApps.repairHandoff(clientId);
      setMsg(r.handoff.created
        ? "Sent — new client created in Alief Dispute Hub."
        : "Sent — matched to their existing Alief Dispute Hub file.");
      setRepair(await fundingApps.repairStatus(clientId));
    } catch (e: any) { setMsg(e?.message || "Handoff failed."); }
    finally { setRepairBusy(false); }
  }

  async function toggleStep(step: any) {
    try {
      await fundingApps.builderToggle(clientId, step.id, !step.done);
      setBuilder(await fundingApps.builder(clientId));
    } catch (e: any) { setMsg(e?.message || "Could not update the step."); }
  }

  const results: any[] = recs?.results || [];

  return (
    <>
      {msg && <p className="muted small" style={{ marginBottom: 12 }}>{msg}</p>}

      {/* actions row */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        <div className="profile-tabs" role="tablist" aria-label="Offer path" style={{ marginBottom: 0 }}>
          {(["both", "personal", "business"] as const).map((p) => (
            <button key={p} role="tab" aria-selected={path === p} className={path === p ? "selected" : undefined}
              onClick={() => { setPath(p); loadRecs(p); }}>
              {p === "both" ? "Both paths" : p === "personal" ? "Personal funding" : "Business funding"}
            </button>
          ))}
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer" }}
          title="Hide lenders that require an in-person branch visit.">
          <input type="checkbox" checked={onlineOnly}
            onChange={(e) => { setOnlineOnly(e.target.checked); loadRecs(path, e.target.checked); }} />
          <span className="muted small">Online only</span>
        </label>
        <span style={{ flex: 1 }} />
        {offersEnabled !== null && (
          <button className="ghost-button" onClick={togglePortalOffers}
            title="Controls whether THIS client sees the offers page in their portal. Off = they only see readiness, you keep control of where they apply.">
            {offersEnabled ? <Eye size={14} aria-hidden="true" /> : <EyeOff size={14} aria-hidden="true" />}
            {" "}Portal offers: {offersEnabled ? "ON" : "OFF"}
          </button>
        )}
        <button className="ghost-button" onClick={() => setAddOpen(true)}><Plus size={14} aria-hidden="true" /> Add product</button>
        {softpullOn && (
          <button className="ghost-button" disabled={pulling || creditAuthorized === false} onClick={runSoftpull}
            title={creditAuthorized === false
              ? "This client has not signed the credit authorization yet. They can sign it in their portal under Credit authorization."
              : "Soft pull through iSoftpull — no impact on their score."}>
            <RefreshCw size={14} aria-hidden="true" />{" "}
            {pulling ? "Pulling…" : creditAuthorized === false ? "Soft pull — needs their signed authorization" : "Soft pull (no score impact)"}
          </button>
        )}
        <button className="primary-button" onClick={openPacket}><Send size={14} aria-hidden="true" /> Send application packet</button>
      </div>

      {/* recommendations */}
      {loading && <p className="muted small">Loading recommendations…</p>}
      {!loading && recs && !recs.analyzed && (
        <div className="panel" style={{ textAlign: "center", padding: 32 }}>
          <Sparkles size={30} aria-hidden="true" style={{ color: "var(--ne-chrome-2)", marginBottom: 8 }} />
          <p className="muted small">{recs.message || "Import and analyze a credit report to unlock application recommendations."}</p>
        </div>
      )}
      {!loading && results.map((res: any) => (
        <div className="panel" key={res.path} style={{ marginBottom: 16 }}>
          <div className="panel-header">
            <div>
              <h2>{res.path === "personal" ? "Personal funding — loans & cards" : "Business funding — business credit cards"}</h2>
              <span className="muted small">
                {res.available
                  ? `Score used: ${res.score_used} (${res.band}) · ${res.recommendations.length} products matched`
                  : "Path gated"}
              </span>
            </div>
          </div>

          {!res.available && (
            <div className="sub-panel" style={{ borderLeft: "3px solid var(--ne-red-bright)" }}>
              <p style={{ margin: 0 }}><ShieldAlert size={14} aria-hidden="true" style={{ verticalAlign: -2 }} /> {res.gate_reason}</p>
            </div>
          )}

          {res.available && (() => {
            const groups: Array<{ key: string; label: string; items: any[] }> =
              res.path === "personal"
                ? [
                    { key: `${res.path}-loans`, label: "Loans", items: res.recommendations.filter((r: any) => r.product_type === "personal_loan") },
                    { key: `${res.path}-cards`, label: "Credit cards", items: res.recommendations.filter((r: any) => r.product_type === "personal_card") },
                  ]
                : [{ key: `${res.path}-cards`, label: "", items: res.recommendations }];
            return groups.filter((g) => g.items.length).map((g) => {
              const open = !!showAll[g.key];
              const visible = open ? g.items : g.items.slice(0, 5);
              return (
                <div key={g.key} style={{ marginBottom: 10 }}>
                  {g.label && <strong style={{ display: "block", margin: "8px 0 4px" }}>{g.label} <span className="muted small">({g.items.length})</span></strong>}
                  <div className="table-list">
                    {visible.map((r: any) => {
                      const t = TIER_STYLE[r.tier] || TIER_STYLE.match;
                      return (
                        <div className="table-row table-row--wide" key={r.product_id} style={{ alignItems: "flex-start" }}>
                          <div style={{ flex: "1 1 340px", minWidth: 0 }}>
                            <strong>{r.name}</strong> <span className="muted small">· {r.issuer}</span>{" "}
                            <span className={`pill ${t.cls}`}>{t.label}</span>
                            {r.highlights && <p className="muted small" style={{ margin: "3px 0 0" }}>{r.highlights}</p>}
                            {r.reasons.slice(0, 2).map((x: string, i: number) => (
                              <p key={i} className="small" style={{ margin: "4px 0 0", color: "var(--ne-success-text)" }}>✓ {x}</p>
                            ))}
                            {r.warnings.map((x: string, i: number) => (
                              <p key={i} className="small" style={{ margin: "4px 0 0", color: "var(--ne-warning-text)" }}>⚠ {x}</p>
                            ))}
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                            {typeof r.annual_fee === "number" && (
                              <span className="muted small">{r.annual_fee === 0 ? "No annual fee" : `$${r.annual_fee}/yr`}</span>
                            )}
                            {r.prequal_url && (
                              <a className="primary-button" style={{ padding: "6px 12px" }} href={r.prequal_url} target="_blank" rel="noopener noreferrer">
                                Check rate (soft pull)
                              </a>
                            )}
                            {r.apply_url && (
                              <a className="ghost-button" href={r.apply_url} target="_blank" rel="noopener noreferrer">
                                Apply <ExternalLink size={12} aria-hidden="true" />
                              </a>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {g.items.length > 5 && (
                    <button className="ghost-button" style={{ marginTop: 6 }}
                      onClick={() => setShowAll((sa) => ({ ...sa, [g.key]: !open }))}>
                      {open ? "Show top 5 only" : `Show all ${g.items.length}`}
                    </button>
                  )}
                </div>
              );
            });
          })()}

          {res.disclaimers?.length > 0 && (
            <p className="muted small" style={{ marginTop: 10 }}>{res.disclaimers.join(" ")}</p>
          )}
        </div>
      ))}

      {/* packet history */}
      {packets.length > 0 && (
        <div className="panel" style={{ marginBottom: 16 }}>
          <div className="panel-header"><div><h2>Application packets sent</h2></div></div>
          <div className="table-list">
            {packets.map((p: any) => (
              <div className="table-row table-row--wide" key={p.id}>
                <strong style={{ flex: "1 1 200px" }}>{p.lender_name || p.sent_to}</strong>
                <span className="muted small">{p.sent_to}</span>
                <span className="muted small">{(p.doc_keys || []).length} docs</span>
                <span className="muted small">{new Date(p.sent_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* credit repair lane */}
      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-header">
          <div>
            <h2><Wrench size={16} aria-hidden="true" style={{ verticalAlign: -2 }} /> Credit repair</h2>
            <span className="muted small">When the report shows real damage, tag the client and hand them to Alief Dispute Hub — they stay your client while the file gets cleaned, then come back for funding.</span>
          </div>
          {repair?.tagged_at && <span className="pill pill-red">Tagged for repair</span>}
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          {!repair?.tagged_at ? (
            <button className="ghost-button" disabled={repairBusy} onClick={() => repairTag(true)}>
              <Wrench size={14} aria-hidden="true" /> Tag for credit repair
            </button>
          ) : (
            <button className="ghost-button" disabled={repairBusy} onClick={() => repairTag(false)}>Remove tag</button>
          )}
          <button className="primary-button" disabled={repairBusy} onClick={repairHandoff}>
            <Send size={14} aria-hidden="true" /> {repair?.handoff_at ? "Re-send to Alief Dispute Hub" : "Send to Alief Dispute Hub"}
          </button>
          {repair?.handoff_at && (
            <span className="muted small">
              Sent {new Date(repair.handoff_at).toLocaleDateString()}
              {repair?.handoff?.created === false ? " (matched existing file)" : ""}
            </span>
          )}
        </div>
      </div>

      {/* business credit builder */}
      <div className="panel">
        <div className="panel-header">
          <div>
            <h2><Hammer size={16} aria-hidden="true" style={{ verticalAlign: -2 }} /> Business Credit Builder</h2>
            <span className="muted small">Foundation → net-30 vendors → tiers → Paydex 80 → bank cards. The client sees this progress in their portal.</span>
          </div>
          {builder?.initialized && <span className="pill pill-green">{builder.progress_pct}% complete</span>}
        </div>
        {!builder?.initialized && (
          <div style={{ textAlign: "center", padding: 20 }}>
            <p className="muted small" style={{ marginBottom: 12 }}>
              Start the guided business-credit track for this client — 20 steps from entity paperwork to a real business line of credit.
            </p>
            <button className="primary-button" onClick={async () => { await fundingApps.builderInit(clientId); setBuilder(await fundingApps.builder(clientId)); }}>
              Start the builder track
            </button>
          </div>
        )}
        {builder?.initialized && (
          <div>
            {Object.entries(builder.phase_labels as Record<string, string>).map(([phase, label]) => {
              const steps = builder.steps.filter((s: any) => s.phase === phase);
              if (!steps.length) return null;
              return (
                <div key={phase} style={{ marginBottom: 14 }}>
                  <strong style={{ display: "block", marginBottom: 6 }}>{label}</strong>
                  <div className="table-list">
                    {steps.map((s: any) => (
                      <div className="table-row table-row--wide" key={s.id} style={{ alignItems: "flex-start", cursor: "pointer" }} onClick={() => toggleStep(s)}>
                        <CheckCircle2 size={18} aria-hidden="true"
                          style={{ color: s.done ? "var(--ne-success-text)" : "var(--ne-line)", flex: "0 0 auto", marginTop: 2 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ textDecoration: s.done ? "line-through" : "none", opacity: s.done ? 0.7 : 1 }}>{s.label}</span>
                          <p className="muted small" style={{ margin: "2px 0 0" }}>{s.detail}</p>
                        </div>
                        {s.done && s.done_at && <span className="muted small">{String(s.done_at).slice(0, 10)}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* add product */}
      <Modal open={addOpen} title="Add your own product" onClose={() => setAddOpen(false)}
        footer={<>
          <button className="ghost-button" onClick={() => setAddOpen(false)}>Cancel</button>
          <button className="primary-button" disabled={addBusy || !np.name.trim() || !np.issuer.trim()} onClick={addProduct}>
            {addBusy ? "Adding…" : "Add product"}
          </button>
        </>}>
        <div className="stacked-form">
          <div className="form-row-2">
            <label className="form-field"><span>Type</span>
              <select value={np.product_type} onChange={(e) => setNp({ ...np, product_type: e.target.value })}>
                <option value="business_card">Business credit card</option>
                <option value="personal_card">Personal credit card</option>
                <option value="personal_loan">Personal loan</option>
              </select></label>
            <label className="form-field"><span>Realistic score floor</span>
              <input value={np.min_score} onChange={(e) => setNp({ ...np, min_score: e.target.value })} inputMode="numeric" /></label>
          </div>
          <div className="form-row-2">
            <label className="form-field"><span>Product name</span>
              <input value={np.name} onChange={(e) => setNp({ ...np, name: e.target.value })} placeholder="Business Rewards Visa" /></label>
            <label className="form-field"><span>Issuer / lender</span>
              <input value={np.issuer} onChange={(e) => setNp({ ...np, issuer: e.target.value })} placeholder="Your local bank" /></label>
          </div>
          <div className="form-row-2">
            <label className="form-field"><span>Application link</span>
              <input value={np.application_url} onChange={(e) => setNp({ ...np, application_url: e.target.value })} placeholder="https://" /></label>
            <label className="form-field"><span>Prequal link (optional)</span>
              <input value={np.prequal_url} onChange={(e) => setNp({ ...np, prequal_url: e.target.value })} placeholder="https:// soft-pull check page" /></label>
          </div>
          <label className="form-field"><span>One-line pitch (optional)</span>
            <input value={np.highlights} onChange={(e) => setNp({ ...np, highlights: e.target.value })} /></label>
          {np.product_type === "business_card" && (
            <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer" }}>
              <input type="checkbox" checked={np.reports_to_personal} onChange={(e) => setNp({ ...np, reports_to_personal: e.target.checked })} />
              <span className="muted small">This card reports activity to PERSONAL credit (adds the disclaimer automatically)</span>
            </label>
          )}
          <p className="muted small">Only your company sees products you add — they rank right alongside the built-in database.</p>
        </div>
      </Modal>

      {/* packet dialog */}
      <Modal open={packetOpen} title="Send application packet" onClose={() => setPacketOpen(false)}
        footer={<>
          <button className="ghost-button" onClick={() => setPacketOpen(false)}>Cancel</button>
          <button className="primary-button" disabled={pBusy || !pEmail.includes("@")} onClick={sendPacket}>
            {pBusy ? "Sending…" : "Send packet"}
          </button>
        </>}>
        <div className="stacked-form">
          <label className="form-field"><span>Lender (optional — names the packet)</span>
            <select value={pLender} onChange={(e) => setPLender(e.target.value)}>
              <option value="">— none —</option>
              {lenderList.map((l: any) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select></label>
          <label className="form-field"><span>Send to (lender/rep email)</span>
            <input value={pEmail} onChange={(e) => setPEmail(e.target.value)} inputMode="email" placeholder="funding@lender.com" /></label>
          <label className="form-field"><span>Note (optional)</span>
            <input value={pNote} onChange={(e) => setPNote(e.target.value)} placeholder="Anything the rep should know" /></label>
          <div>
            <span className="muted small" style={{ display: "block", marginBottom: 6 }}>Attach client documents ({pDocs.size} selected) — checking a document confirms you opened it and know what is in it. Identity and tax paperwork (licence, SS card, W-2, 1040) asks for a second confirmation before it goes out.</span>
            <div className="table-list" style={{ maxHeight: 180, overflowY: "auto" }}>
              {uploads.length === 0 && <p className="muted small">No client uploads on file.</p>}
              {uploads.map((u: any) => (
                <label key={u.id} className="table-row table-row--wide" style={{ cursor: "pointer", alignItems: "center", gap: 8 }}>
                  <input type="checkbox" checked={pDocs.has(u.id)} onChange={(e) => {
                    const next = new Set(pDocs);
                    e.target.checked ? next.add(u.id) : next.delete(u.id);
                    setPDocs(next);
                  }} />
                  <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{u.original_filename}</span>
                  <span className="muted small">{u.bytes ? `${Math.round(u.bytes / 1024)} KB` : ""}</span>
                </label>
              ))}
            </div>
            <p className="muted small" style={{ marginTop: 6 }}>
              The packet carries the funding summary (never SSN, DOB, or credentials) plus these attachments.
              By checking a document you confirm you opened it and it carries no SSN or date of birth — nothing attaches without that per-document confirmation.
              Identity and tax documents (driver&#39;s license, SS card, tax return) are off by default and ask for a second confirmation naming the file — include one only if your banking relationship manager asked for it. It is recorded in the audit log.
            </p>
          </div>
        </div>
      </Modal>
    </>
  );
}
